import React, { useState } from "react";
import { login } from "./auth";
import "./Login.css";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider"; // Import the auth context
import { useTranslation } from "react-i18next";
import { FRONTEND_URL } from "../../utils/config";

function LoginForm({ setIsResetPass, setIsRegister }) {
  const { t } = useTranslation("login");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [dirty, setDirty] = useState(false);
  const navigate = useNavigate();

  // Get the login function from AuthProvider
  const { login: contextLogin } = useAuth();

  const validate = (data) => {
    const errs = {};
    if (!data.email.trim()) {
      errs.email = t("loginForm.validation.emailRequired");
    } else if (
      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(data.email)
    ) {
      errs.email = t("loginForm.validation.emailInvalid");
    }
    if (!data.password) {
      errs.password = t("loginForm.validation.passwordRequired");
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDirty(true);
    const newData = { ...formData, [name]: value };
    setFormData(newData);
    setErrors(validate(newData));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    setErrors(validationErrors);
    setTouched({ email: true, password: true });
    if (Object.keys(validationErrors).length > 0) {
      return;
    }
    try {
      setLoginError("");
      setLoading(true);
      const response = await login(formData);
      if (response && response.data) {
        const userData = response.data?.data;
        // Update the auth context state
        contextLogin(userData);

        // (Optional) Also store token and user in localStorage
        // localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));

        // Redirect to dashboard after successful login
        navigate("/dashboard");
      } else {
        setLoginError(t("loginForm.loginError"));
      }
    } catch (error) {
      console.error("Login error: ", error);
      let errorMessage =
        error?.response?.error ||
        error?.response?.message ||
        t("loginForm.genericError");
      if (typeof errorMessage === "object") {
        errorMessage = errorMessage.message || JSON.stringify(errorMessage);
      }
      setLoginError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSuppliersRedirect = () => {
    window.location.href = `${FRONTEND_URL}/contact`;
  };

  return (
    <form onSubmit={onSubmit} className="login-form">
      <h1 className="title-login-form">{t("loginForm.title")}</h1>
      <p className="subtitle-login-form">{t("loginForm.subtitle")}</p>

      <div className="form-group-login">
        <label htmlFor="email">{t("loginForm.labels.email")}</label>
        <input
          type="text"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {touched.email && errors.email && (
          <p className="login-error">{errors.email}</p>
        )}
      </div>

      <div className="form-group-login">
        <label htmlFor="password">{t("loginForm.labels.password")}</label>
        <div className="password-input-container">
          <input
            type={formData.passwordVisible ? "text" : "password"}
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          <span
            className="eye-icon"
            onClick={() =>
              setFormData({
                ...formData,
                passwordVisible: !formData.passwordVisible,
              })
            }
          >
            <img
              src={formData.passwordVisible ? "/eye.svg" : "/eye-slash.svg"}
              alt={t("loginForm.alt.toggleVisibility")}
            />
          </span>
        </div>
        {touched.password && errors.password && (
          <p className="login-error">{errors.password}</p>
        )}
      </div>

      {(loginError || Object.keys(errors).length > 0) && (
        <p className="login-error">{loginError}</p>
      )}

      <div className="form-group-login">
        <button
          type="button"
          className="forgot-password"
          onClick={() => setIsResetPass(true)}
        >
          {t("loginForm.buttons.forgotPassword")}
        </button>
      </div>

      <div className="form-group-login">
        <button
          type="submit"
          disabled={loading || Object.keys(errors).length > 0 || !dirty}
          className="login-btn"
        >
          {loading
            ? t("loginForm.buttons.loggingIn")
            : t("loginForm.buttons.login")}
        </button>
      </div>

      <div className="form-group-login">
        <button
          className="register-btn"
          type="button"
          onClick={handleSuppliersRedirect}
        >
          {t("loginForm.buttons.joinUs")}
        </button>
      </div>
    </form>
  );
}

export default LoginForm;
